import { useEffect, useMemo, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api } from "../lib/api";

type Preview = {
  headers: string[];
  mapping: Record<string, string>;
  unmapped: string[];
  sample: Array<{
    phone_in?: string;
    phone_out?: string;
    valid: boolean;
    first_name?: string;
    last_name?: string;
    email?: string;
    state?: string;
    tags: string[];
    custom_fields: Record<string, string>;
    /** The row exactly as the file has it, keyed by header. */
    raw?: Record<string, string>;
  }>;
};

type Target = { key: string; label: string; required?: boolean };

type Report = {
  total_rows: number;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  reasons: Record<string, number>;
  /** Up to ten skipped rows, with the row number and the record as it was in
   *  the file. The API has always returned these. */
  examples?: Array<{ row: number; reason: string; data: Record<string, string> }>;
  batch_tag?: string | null;
  consent?: { granted: number; already: number; refused_opted_out: number };
};

export function Import() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One tag on every row in the file. Without it the only handle the audience
  // builder has on "the people from that CSV" is a tags column pasted into
  // every line by hand.
  const [batchTag, setBatchTag] = useState("");
  // Where the consent came from. Deliberately free text and deliberately
  // required to be typed: consent is not importable as a column, because a
  // spreadsheet is not a consent record, but naming its real source is what
  // makes the claim auditable later.
  const [consentSource, setConsentSource] = useState("");

  useEffect(() => {
    api.importTargets().then(setTargets).catch((e) => setError(e.message));
  }, []);

  const choose = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setReport(null);
    setError(null);
    if (!f) return;
    setBusy(true);
    api
      .importPreview(f)
      .then((p: Preview) => {
        setPreview(p);
        // start from what we guessed; the dropdowns are the correction
        setMapping(p.mapping);
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  // Phone is how contacts are identified, so an import without one cannot
  // proceed. Saying so up front beats failing after the upload.
  const phoneColumn = useMemo(
    () => Object.entries(mapping).find(([, v]) => v === "phone")?.[0],
    [mapping],
  );

  const duplicates = useMemo(() => {
    const seen: Record<string, number> = {};
    for (const v of Object.values(mapping)) {
      if (v && v !== "tags") seen[v] = (seen[v] ?? 0) + 1;
    }
    return Object.entries(seen)
      .filter(([, n]) => n > 1)
      .map(([k]) => k);
  }, [mapping]);

  // Tags are keys, not prose: lowercased, spaces to underscores, so what you
  // type here is what the audience builder will offer you later.
  const tagKey = batchTag.trim().toLowerCase().replace(/\s+/g, "_").slice(0, 64);

  const run = () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    api
      .runImport(file, false, mapping, tagKey, consentSource.trim() || undefined)
      .then(setReport)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <ErrorBanner error={error} />
      <h2 style={{ fontSize: 19 }}>Import contacts</h2>

      <Card title="Choose a file">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
        />
        {busy && !preview && (
          <div className="dim" style={{ marginTop: 10 }}>
            Reading…
          </div>
        )}
      </Card>

      {preview && (
        <>
          <Card
            title="Match your columns"
            footer={
              <span className="dim">
                We guessed from the column names. Correct anything that is wrong — the
                dropdown is what gets used, not the guess.
              </span>
            }
          >
            {!phoneColumn && (
              <div className="banner bad" style={{ marginBottom: 12 }}>
                <b>No phone column.</b> Contacts are identified by phone number, so one
                column has to be mapped to it.
              </div>
            )}
            {duplicates.length > 0 && (
              <div className="banner warn" style={{ marginBottom: 12 }}>
                Two columns are both mapped to{" "}
                <b>{duplicates.join(", ")}</b>. The last one wins, which is probably not
                what you want.
              </div>
            )}

            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Column in your file</th>
                    <th>Example value</th>
                    <th>Import as</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((h) => {
                    // The raw row, not custom_fields. custom_fields holds
                    // only the columns that were NOT mapped, so this column -
                    // whose entire job is to let you check the guess against
                    // real data - was blank for every column that had a guess.
                    const example =
                      preview.sample[0]?.raw?.[h] ??
                      (preview.sample[0]?.custom_fields ?? {})[
                        h.toLowerCase().replace(/[-\s]/g, "_")
                      ] ??
                      "";
                    const value = mapping[h] ?? "";
                    return (
                      <tr
                        key={h}
                        style={
                          value === "__skip__"
                            ? { opacity: 0.45, textDecoration: "line-through" }
                            : undefined
                        }
                      >
                        <td className="mono" style={{ fontWeight: 600 }}>
                          {h}
                        </td>
                        <td className="dim mono" style={{ fontSize: 11 }}>
                          {example || "—"}
                        </td>
                        <td>
                          <select
                            className="f"
                            style={{
                              minWidth: 210,
                              borderColor:
                                value === "phone" ? "var(--acc)" : undefined,
                            }}
                            value={value}
                            onChange={(e) =>
                              setMapping((m) => ({ ...m, [h]: e.target.value }))
                            }
                          >
                            {targets.map((t) => (
                              <option key={t.key} value={t.key}>
                                {t.label}
                                {t.required ? " *" : ""}
                              </option>
                            ))}
                          </select>
                          {/* One click to drop a column. It is in the dropdown
                              too, but a CRM export has a lot of columns you do
                              not want and hunting the same option in every row
                              is the slow way to say no. */}
                          <button
                            className="btn s"
                            title="Do not import this column"
                            aria-label={`Do not import ${h}`}
                            style={{ marginLeft: 6 }}
                            onClick={() =>
                              setMapping((m) => ({
                                ...m,
                                [h]: m[h] === "__skip__" ? "" : "__skip__",
                              }))
                            }
                          >
                            {value === "__skip__" ? "undo" : "×"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card
            title="The first rows, as they would import"
            footer={
              <span className="dim">
                Rows without a usable phone number are recorded and skipped, never
                imported half-formed. Existing people are matched on phone and updated,
                not duplicated.
              </span>
            }
          >
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Phone in file</th>
                    <th>Stored as</th>
                    <th>Name</th>
                    <th>State</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i} style={{ opacity: r.valid ? 1 : 0.55 }}>
                      <td>
                        <Pill tone={r.valid ? "ok" : "ba"}>
                          {r.valid ? "ok" : "skip"}
                        </Pill>
                      </td>
                      <td className="mono dim">{r.phone_in ?? "—"}</td>
                      <td className="mono">{r.phone_out ?? "cannot read"}</td>
                      <td>
                        {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td>{r.state ?? "—"}</td>
                      <td className="dim mono" style={{ fontSize: 10.5 }}>
                        {r.tags.join(", ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ marginTop: 14, flexWrap: "wrap", gap: 8 }}>
              <span className="lab">tag everyone in this file</span>
              <input
                className="f"
                value={batchTag}
                placeholder="e.g. boss_list_sep4"
                onChange={(e) => setBatchTag(e.target.value)}
              />
              <span className="lab">consent came from</span>
              <input
                className="f"
                style={{ minWidth: 220 }}
                value={consentSource}
                placeholder="e.g. GHL export, app signups"
                onChange={(e) => setConsentSource(e.target.value)}
              />
            </div>

            {consentSource.trim() && !tagKey && (
              <div className="banner warn stack" style={{ marginTop: 10 }}>
                <b>Consent needs a tag.</b> Consent is recorded over the people
                this import tagged, so name a tag above or the consent step is
                skipped.
              </div>
            )}

            {tagKey && !consentSource.trim() && (
              <div className="banner warn stack" style={{ marginTop: 10 }}>
                <b>These contacts will not be sendable yet.</b> An imported
                contact has no consent recorded, and the send gate holds those
                back - the import will look like it worked and nothing will go
                out. Say where the consent came from to record it now, or do it
                afterwards from the Audiences screen.
              </div>
            )}

            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn p" disabled={busy || !phoneColumn} onClick={run}>
                {busy ? "Importing…" : "Import these contacts"}
              </button>
              {!phoneColumn && (
                <span className="dim" style={{ fontSize: 12 }}>
                  Map a column to the phone number first.
                </span>
              )}
            </div>
          </Card>
        </>
      )}

      {report && (
        <Card title="Done">
          <div className="tiles">
            <div className="tile">
              <span className="lab">Rows read</span>
              <span className="v">{fmt.n(report.total_rows)}</span>
            </div>
            <div className="tile ok">
              <span className="lab">Added</span>
              <span className="v">{fmt.n(report.created)}</span>
            </div>
            <div className="tile">
              <span className="lab">Updated</span>
              <span className="v">{fmt.n(report.updated)}</span>
            </div>
            {/* The fifth bucket. The API has always returned it and this
                card rendered four tiles, so re-importing a file everybody was
                already in showed "9 rows read, 0 added, 0 updated, 5 skipped"
                and four rows simply vanished. */}
            <div className="tile">
              <span className="lab">Already up to date</span>
              <span className="v">{fmt.n(report.unchanged)}</span>
            </div>
            <div className="tile ba">
              <span className="lab">Skipped</span>
              <span className="v">{fmt.n(report.skipped)}</span>
            </div>
            {report.consent && (
              <div className="tile ok">
                <span className="lab">Consent recorded</span>
                <span className="v">{fmt.n(report.consent.granted)}</span>
              </div>
            )}
          </div>

          {/* The import is only half the job. Without saying where the list
              went, the next question - "so how do I text them?" - has no
              answer on this screen. */}
          {report.batch_tag && (
            <div className="banner stack" style={{ marginTop: 12 }}>
              <b>Tagged “{report.batch_tag}”.</b> To message them: Audiences →
              add a condition <b>Tag is {report.batch_tag}</b> → check the
              reachable count → Create blast.
              {!report.consent && (
                <>
                  {" "}
                  These contacts have no consent recorded yet, so the reachable
                  count will be 0 and nothing would send. Record it from the
                  Audiences screen first.
                </>
              )}
              {report.consent && report.consent.refused_opted_out > 0 && (
                <>
                  {" "}
                  {fmt.n(report.consent.refused_opted_out)} of them had
                  previously opted out and were left alone.
                </>
              )}
            </div>
          )}

          {Object.keys(report.reasons).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <span className="lab">Why rows were skipped</span>
              {Object.entries(report.reasons).map(([k, v]) => (
                <div className="brk" key={k}>
                  <span className="dim">{k.replace(/_/g, " ")}</span>
                  <span>{fmt.n(v)}</span>
                </div>
              ))}

              {/* Which rows, not just how many. A count tells you something
                  went wrong; the row number and the actual values are what
                  let you fix the file and run it again. The API computes
                  these and the screen was discarding them. */}
              {report.examples && report.examples.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <span className="lab">
                    The first {report.examples.length} of them
                  </span>
                  <div className="tw">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Why</th>
                          <th>What was in the file</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.examples.map((ex) => (
                          <tr key={ex.row}>
                            <td className="mono">{ex.row}</td>
                            <td className="dim">{ex.reason.replace(/_/g, " ")}</td>
                            <td className="mono" style={{ fontSize: 11 }}>
                              {Object.entries(ex.data)
                                .filter(([, v]) => String(v ?? "").trim())
                                .map(([k, v]) => `${k}: ${v}`)
                                .join("  ·  ") || "(the row was empty)"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="row" style={{ marginTop: 14 }}>
            <a className="btn p" href="/contacts">
              See the contacts
            </a>
            {/* Carries the tag. It used to be a bare link to /audiences,
                which dropped you on the default filter and left you to
                retype the condition the banner above had just named. */}
            <a
              className="btn"
              href={
                report.batch_tag
                  ? `/audiences?tag=${encodeURIComponent(report.batch_tag)}`
                  : "/audiences"
              }
            >
              {report.batch_tag
                ? `Build an audience from “${report.batch_tag}”`
                : "Build an audience"}
            </a>
          </div>
        </Card>
      )}
    </>
  );
}
